/**
 * Consolidated authentication functions for client and client-admin
 */
import {
  client as axios,
  publicClient,
  createClient,
  authedClient,
} from "./axios";
import { isEmpty, isFunction, isString } from "lodash";
import {
  loginFailure,
  loginSuccess,
  loginUserSuccess,
  logout as storeLogout,
  logoutUser as storeLogoutUser,
} from "./store";

const tokenExpirationDays = 30; // Default expiration for cookies (days) - aligned with server sanctum.expiration
export const TOKEN_KEY = "token-smfa";

// Check if we are in a browser environment
const isBrowser = Boolean(
  typeof window !== "undefined" && typeof document !== "undefined",
);

/**
 * Cookie utility functions for token management
 */
const CookieUtils = {
  /**
   * Get cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  getCookie(name) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
  },

  /**
   * Set cookie with appropriate security settings
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Cookie expiration in days
   */
  setCookie(name, value, days = tokenExpirationDays) {
    const isSecure = Boolean(
      isBrowser && window.location.protocol === "https:",
    );
    const expires = new Date(
      Date.now() + days * 24 * 60 * 60 * 1000,
    ).toUTCString();

    // Build cookie attributes
    const cookieAttrs = [
      `${name}=${encodeURIComponent(value)}`,
      `expires=${expires}`,
      "path=/",
      isSecure ? "Secure" : "", // Only use Secure flag in HTTPS environments
      "SameSite=Lax", // SameSite=Lax allows cookies to be sent in first-party context and with GET requests
    ]
      .filter(Boolean)
      .join("; ");

    if (isBrowser) {
      document.cookie = cookieAttrs;
    }
  },

  /**
   * Remove cookie by setting expiration in the past
   * @param {string} name - Cookie name
   */
  removeCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  },
};

/**
 * Token utilities for consistent authentication across applications
 */
export const TokenManager = {
  /**
   * Validate token with server and return validation status
   * @returns {Promise} Object containing validation result
   */
  validateToken() {
    const token = CookieUtils.getCookie(TOKEN_KEY);

    if (!token) {
      log.log("[utils] TokenManager:validateToken() - No token found");
      return Promise.resolve({
        valid: false,
        message: "No token found",
      });
    }

    log.log(
      "[utils] TokenManager:validateToken() - Validating token with server (token present:",
      Boolean(token),
      ")",
    );

    // Use the authenticated client for the validation call
    return authedClient
      .get("/user/token-validate")
      .then((response) => {
        if (response.data && response.data.status === true) {
          log.log(
            "[utils] TokenManager:validateToken() - Token is valid",
            response.data,
          );

          return {
            success: true,
            auth: response.data.auth,
            message: response.data.message || "Token is valid",
          };
        } else {
          log.warn("[utils] TokenManager:validateToken() - Token is invalid");
          // Clean up invalid token
          this.clearToken();

          return {
            success: false,
            message: response.data.message || "Token validation failed",
          };
        }
      })
      .catch((error) => {
        log.error(
          "[utils] TokenManager:validateToken() - Validation error:",
          error,
        );

        // If we get a 401/403, the token is definitely invalid
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 403)
        ) {
          this.clearToken();

          return {
            success: false,
            message: "Token is invalid or expired",
            error,
          };
        }

        // For other errors (like network issues), we don't immediately invalidate
        return {
          success: false,
          message: "Token validation error",
          error,
          // Flag to indicate this wasn't a token validity problem but a technical error
          isConnectionError: true,
        };
      });
  },

  /**
   * Get token with proper error handling
   * @param {string} role - "admin" or "member"
   * @returns {string|null} The authentication token
   */
  getToken() {
    try {
      const token = CookieUtils.getCookie(TOKEN_KEY);
      // No token found
      if (!token) {
        return null;
      }

      // Handle JSON string tokens
      if (
        typeof token === "string" &&
        (token.startsWith("{") || token.startsWith("["))
      ) {
        try {
          const parsedToken = JSON.parse(token);
          log.log(`Parsed token from JSON:`, typeof parsedToken);

          // Special handling for Laravel Sanctum token format
          if (parsedToken.id && parsedToken.tokenable_id) {
            log.info(
              `Detected Sanctum token format with ID: ${parsedToken.id}`,
            );
            return String(parsedToken.id);
          }

          // Extract token from different object formats
          if (parsedToken.id) {
            log.info(`Using token.id: ${parsedToken.id}`);
            return String(parsedToken.id);
          }
          if (parsedToken.token) {
            log.info(
              `Using token.token: ${parsedToken.token.substring(0, 10)}...`,
            );
            return String(parsedToken.token);
          }
          if (parsedToken.access_token) {
            log.info(
              `Using token.access_token: ${parsedToken.access_token.substring(0, 10)}...`,
            );
            return String(parsedToken.access_token);
          }

          // Return the first string property found if none of the expected ones exist
          for (const key in parsedToken) {
            if (typeof parsedToken[key] === "string") {
              log.info(
                `Using token.${key}: ${parsedToken[key].substring(0, 10)}...`,
              );
              return parsedToken[key];
            }
          }

          log.warn(
            "No string properties found in token object, using ID property",
          );
          return String(parsedToken.id || "").trim();
        } catch (e) {
          log.warn("Failed to parse token JSON:", e);
          return token;
        }
      }

      // Return the token as string
      return String(token).trim();
    } catch (e) {
      log.error("Error retrieving token:", e);
      return null;
    }
  },

  /**
   * Set token with cookie storage
   * @param {string|object} token - The token to store
   * @param {string} role - "admin" or "member"
   */
  setToken(token) {
    log.info(`TokenManager:setToken()`, typeof token);

    if (!token) {
      CookieUtils.removeCookie(TOKEN_KEY);
      return;
    }

    try {
      // For objects, stringify them to ensure consistent format
      let tokenToStore = token;
      if (typeof token === "object" && token !== null) {
        tokenToStore = JSON.stringify(token);
        log.info("Converting object token to JSON string for storage");
      }

      // Store in cookie
      CookieUtils.setCookie(TOKEN_KEY, tokenToStore, tokenExpirationDays);
      log.info(`Token stored for in cookie`);
    } catch (e) {
      log.error("Error storing token:", e);
    }
  },

  /**
   * Refresh token with server and update local storage
   * @param {Object} options - Options for token refresh
   * @param {Function} options.dispatch - Optional Redux dispatch function
   * @returns {Promise} Object containing refresh result
   */
  refreshToken(options = {}) {
    const currentToken = this.getToken();

    if (!currentToken) {
      log.info("[utils] TokenManager:refreshToken() - No token to refresh");
      return Promise.resolve({
        success: false,
        message: "No token to refresh",
      });
    }

    log.info(
      "[utils] TokenManager:refreshToken() - Refreshing token with server",
    );

    // Use the authenticated client for the refresh call
    return authedClient
      .post("/user/token-refresh")
      .then((response) => {
        if (response.data && response.data.status === true) {
          const newToken = response.data?.auth?.token;
          const user = response.data?.auth?.user;

          log.info(
            `[utils] TokenManager:refreshToken(): token refreshed successfully`,
          );

          // Store the new token
          this.setToken(newToken);

          return {
            success: true,
            message: `Token refreshed`,
            data: {
              token: newToken,
              user,
            },
          };
        } else {
          log.warn(
            "[utils] TokenManager:refreshToken() - Failed to refresh token",
          );

          return {
            success: false,
            message: response.data.message || "Token refresh failed",
            data: response.data,
          };
        }
      })
      .catch((error) => {
        log.error(
          "[utils] TokenManager:refreshToken() - Refresh error:",
          error,
        );

        // If we get a 401/403, the token is invalid and cannot be refreshed
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 403)
        ) {
          this.clearToken();

          return {
            success: false,
            message: "Token is invalid or expired and cannot be refreshed",
            error,
          };
        }

        // For other errors, don't clear the token
        return {
          success: false,
          message: "Token refresh error",
          error,
          isConnectionError: true,
        };
      });
  },

  /**
   * Clear token from local storage (cookie) only.
   * Server-side revocation is handled by the /user/logout endpoint
   * or explicitly via /user/token-revoke where needed.
   */
  clearToken() {
    log.info("TokenManager:clearToken()");
    CookieUtils.removeCookie(TOKEN_KEY);
  },

  /**
   * Clear all tokens from storage
   */
  clearAllTokens() {
    log.info("TokenManager:clearAllTokens()");
    CookieUtils.removeCookie(TOKEN_KEY);
    log.info("All tokens cleared");
  },
};

/**
 * Authentication actions that can be used with Redux
 * @param {object} client - Axios instance to use for requests
 * @param {string} role - "admin" or "member"
 */
// !!LIKELY DEPRECATED !!
export const createAuthActions = (client, role = "member") => {
  log.info("createAuthActions() class", { client, role });
  return {
    login: async (credentials) => {
      log.info("createAuthActions() login", { credentials });
      try {
        const endpoint = role === "admin" ? "/login_check" : "/user/login";
        const response = await client.post(endpoint, credentials);

        if (response.data.status) {
          // Extract token from response based on role
          const token =
            role === "admin"
              ? response.data.data.token
              : response.data.data.token;

          // Store token in cookie
          TokenManager.setToken(token);

          return {
            success: true,
            data: response.data.data,
            user: response.data.data.user || response.data.data,
            token,
          };
        }

        return {
          success: false,
          message: response.data.message || "Login failed",
        };
      } catch (error) {
        return {
          success: false,
          message: error.response?.data?.message || "Login failed",
          error,
        };
      }
    },

    logout: logout(),
  };
};

/**
 * Authentication utility functions that use the authenticated client
 */

/**
 * Attempt login with credentials with optional redux dispatch
 * @param {Object} credentials - User credentials
 * @param {Object} options - Optional client creation options
 * @param {Function} options.dispatch - Redux dispatch function
 * @param {Function} options.onSuccess - Callback on success
 * @param {Function} options.onError - Callback on error
 * @param {string} options.requireRole - Role required for login
 * @returns {Promise} Login result
 */
export const login = async (credentials, options = {}) => {
  const { dispatch, onSuccess, onError, requireRole = "" } = options;

  try {
    // For login, we don't need the authenticated client since we're getting the token
    // Use a basic axios instance for this specific request
    const response = await publicClient.post("/user/login", credentials);

    if (response.data && response.data.status === true) {
      const token = response.data.data.token;
      const user = response.data.data.user;
      if (!isEmpty(requireRole) && requireRole !== user.role) {
        log.warn("Access Denied: User role does not match required role");
        const message = "Access Denied.  Insufficient Permissions.";
        if (isFunction(onError) && onError) {
          onError({ message });
        }
        if (isFunction(dispatch) && dispatch) {
          dispatch(loginFailure(message));
        }
        return { success: false, message };
      }

      TokenManager.setToken(token);

      // Also store user data for easier access
      if (isBrowser) {
        localStorage.setItem("userData", JSON.stringify(user));
      }

      if (isFunction(dispatch) && dispatch) {
        dispatch(loginSuccess({ token, user }));
        dispatch(loginUserSuccess({ token, user }));
      }

      // Immediately hydrate with latest profile + membership.
      // Without this, login() returns a partial user object (often missing
      // membership), which can cause the dashboard to briefly show
      // "No Active Membership" until a full session restore occurs.
      let hydratedUser = user;
      try {
        hydratedUser = await applyUserData({ dispatch, token, userData: user });
      } catch (e) {
        log.warn("[utils] login(): applyUserData hydration failed", e);
        hydratedUser = user;
      }

      if (isFunction(onSuccess) && onSuccess) {
        onSuccess({ token, user: hydratedUser });
      }

      return {
        success: true,
        data: { token, user: hydratedUser },
      };
    } else {
      const message = response.data.message || "Login failed";
      if (isFunction(onError) && onError) {
        onError({ message, response });
      }
      return { success: false, message };
    }
  } catch (error) {
    const message = error?.response?.data?.message || "Login failed";
    if (isFunction(onError) && onError) {
      onError({ message, response: error?.response });
    }
    log.error("[utils] login() error:", error);
    return { success: false, message };
  }
};

/**
 * Log out the current user
 * !NOTE: Implementing code should have a dispatch type: LOGOUT to work properly
 * @param {Function} dispatch - Optional Redux dispatch function
 * @param {Object} options - Optional client creation options
 * @param {string} options.type - "admin", "member", or (boolean)true for all types
 * @returns {Promise} Logout result
 */
export const logout = async (dispatch = null, options = { type: "member" }) => {
  return logoutUser(dispatch, options);
};

/**
 * Complete logout function that handles both client and server-side logout
 * @param {function} dispatch - Redux dispatch function
 * @returns {Promise} - Promise that resolves when logout is complete
 */
export const logoutUser = (dispatch, options = {}) => {
  const { persistor } = options;
  // Get current token before it's cleared
  const token = TokenManager.getToken();
  log.info("logoutUser():", { isBrowser });

  // 1. Make the API call to invalidate the token server-side
  axios
    .post(
      `/user/logout`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
    .then((response) => {
      log.info("logoutUser(): Server logout successful", response.data);
      return response.data;
    })
    .catch((error) => {
      log.error("logoutUser(): Server logout error", error);
      // Handle the error appropriately
      return Promise.reject(error);
    })
    .finally(() => {
      //
    });
  // Run regardless of server-side logout
  if (isFunction(dispatch) && dispatch) {
    dispatch(storeLogout());
    dispatch(storeLogoutUser());
  }
  if (isBrowser) {
    localStorage.removeItem("userData");
    localStorage.removeItem("@secure.s.browserToken");
    localStorage.removeItem("persist:root");
    if (persistor) {
      log.info("logoutUser(): purging persistor");
      persistor.purge();
    }
  }
  sessionStorage.clear();
  TokenManager.clearToken();
};

/**
 * Get current user profile
 * @param {Object} options - Optional client creation options
 * @see {@link /server/routes/api.php}
 * @see {@link /server/app/Http/Controllers/Api/UserAuthController.php}
 * @returns {Promise} User profile
 */
export const getProfile = async () => authedClient.get("/user/profile");

/**
 * Get current users membership data
 * @param {Object} options - Optional client creation options
 * @see {@link /server/routes/api.php}
 * @see {@link /server/app/Http/Controllers/Api/UserAuthController.php}
 * @returns {Promise} Membership data
 */
export const getMembership = async () => authedClient.get("/user/membership");

/**
 * Current user: Product purchase data (NO membership data)
 * @param {Object} options - Optional client creation options
 * @see {@link /server/routes/api.php}
 * @see {@link /server/app/Http/Controllers/Api/UserAuthController.php}
 * @returns {Promise} Product purchase data
 */
export const getProducts = async () => {
  try {
    return await authedClient.get("/user/products");
  } catch (error) {
    // Backward compatibility: older servers returned 404 when there were
    // simply no purchases. Treat that as an empty list.
    if (error?.response?.status === 404) {
      return {
        data: {
          status: true,
          data: {
            products: [],
          },
        },
      };
    }
    throw error;
  }
};

/**
 * Update current user profile
 * @param {Object} profileData - Profile data to update
 * @param {Object} options - Optional client creation options
 * @returns {Promise} Update result
 */
export const updateProfile = async (profile) =>
  axios.post("/user/profile", profile);

/**
 * Update current user password
 * @param {Object} passwordData - Old and new password
 * @param {Object} options - Optional client creation options
 * @returns {Promise} Update result
 */
export const updatePassword = async (passwordData) =>
  axios.post("/user/password", passwordData);

// -- SESSION MANAGEMENT (tokens, cookies and state) ---------------------------

/**
 * Detect, validate, and initialize user session
 * This should occur as early as reasonable in the app lifecycle
 *
 * @param {Object} options - Options for session initialization
 * @param {Function} options.dispatch - Optional Redux dispatch function
 * @param {Boolean} options.validateWithServer - Whether to validate token with server (default: true)
 * @returns {Promise<Object>} Session initialization result
 */
export const initializeSession = async (options = {}) => {
  const { dispatch, validateWithServer = true } = options;

  // Find token, if exists
  const token = TokenManager.getToken();
  log.info("[utils] initializeSession: Token exists?", Boolean(token));

  if (!token) {
    log.info(
      "[utils] initializeSession: No token found, no session to restore",
    );
    // Clear any stale persisted auth state so the login page doesn't
    // redirect to a protected route (which would bounce back - loop).
    if (dispatch) {
      dispatch(storeLogout());
      dispatch(storeLogoutUser());
    }
    if (isBrowser) {
      localStorage.removeItem("userData");
    }
    return {
      success: false,
      status: "no_token",
      message: "No token found",
    };
  }

  // Get user data from storage
  let userData = null;
  if (isBrowser) {
    const userDataString = localStorage.getItem("userData");
    if (userDataString) {
      userData = JSON.parse(userDataString);
      log.info("[utils] initializeSession: User data found in storage");
    }
  }

  // Validate token with server if requested
  if (validateWithServer) {
    log.info("[utils] initializeSession: Validating token with server");

    try {
      const validationResult = await TokenManager.validateToken();

      if (!validationResult.success) {
        log.warn(
          "[utils] initializeSession: Token validation failed",
          validationResult.message,
        );

        // If we have a connection error, we might still use cached data
        if (validationResult.isConnectionError && userData) {
          log.info("[utils] initializeSession: Cached (connection delayed).");

          // Dispatch to Redux if dispatch function provided
          if (dispatch) {
            dispatch(loginSuccess({ token, user: userData }));
            dispatch(loginUserSuccess({ token, user: userData }));
          }

          return {
            success: true,
            status: "offline_restore",
            message: "Session restored from cache (offline)",
            token,
            user: userData,
          };
        }

        // Otherwise, session is invalid - clear persisted auth state
        log.warn(
          "[utils] initializeSession: Session invalid, dispatching logout.",
          { isConnectionError: validationResult.isConnectionError },
        );
        if (dispatch) {
          dispatch(storeLogout());
          dispatch(storeLogoutUser());
        }
        if (isBrowser) {
          localStorage.removeItem("userData");
        }
        TokenManager.clearToken();

        return {
          success: false,
          status: "invalid_token",
          message: validationResult.message,
        };
      }

      // Use the validated user data from the server as the authoritative
      // identity source, NOT localStorage (which may be stale or belong to
      // a different user -- e.g. when client and admin share a cookie).
      const validatedUser = validationResult.auth?.user || null;
      const finalUserData = await applyUserData({
        dispatch,
        token,
        userData: validatedUser || userData,
      });

      if (finalUserData) {
        return {
          success: true,
          status: "session_restored",
          message: "Session successfully restored",
          token,
          user: finalUserData,
        };
      } else {
        log.warn("[utils] initializeSession: Token valid but no user data");
        // Clear auth state - don't leave the user in a half-authenticated state
        if (dispatch) {
          dispatch(storeLogout());
          dispatch(storeLogoutUser());
        }
        return {
          success: false,
          status: "missing_user_data",
          message: "Token valid but no user data found",
        };
      }
    } catch (error) {
      log.error(
        "[utils] initializeSession: Unexpected error during validation - forcing logout",
        error,
      );
      // Clear auth state on unexpected errors too
      if (dispatch) {
        dispatch(storeLogout());
        dispatch(storeLogoutUser());
      }
      if (isBrowser) {
        localStorage.removeItem("userData");
      }
      TokenManager.clearToken();
      return {
        success: false,
        status: "validation_error",
        message: "Error validating session",
        error,
      };
    }
  } else if (userData) {
    // No validation requested, just restore from storage
    log.info("[utils] initializeSession: Restoring");

    // Dispatch to Redux if dispatch function provided
    if (dispatch) {
      dispatch(loginSuccess({ token, user: userData }));
      dispatch(loginUserSuccess({ token, user: userData }));
    }

    return {
      success: true,
      status: "local_restore",
      message: "Session restored from local storage[2]",
      token,
      user: userData,
    };
  }

  return {
    success: false,
    status: "incomplete_data",
    message: "Token found but no user data[2]",
  };
};

/**
 *
 */
export const validateSession = async (options = {}) =>
  TokenManager.validateToken(options);

/**
 * Refresh session with a new token + user data
 * @param {Object} options - Optional client creation options
 * @param {Function} options.dispatch - Redux dispatch function
 * @returns {Promise} Refresh result
 */
export const refreshSession = async (options = {}) => {
  const { dispatch } = options;
  const res = await TokenManager.refreshToken();
  if (res.success && res?.data?.user) {
    // Update user data in localStorage if available
    if (isBrowser && res.data.user) {
      localStorage.setItem("userData", JSON.stringify(res.data.user));
    }

    if (isFunction(dispatch) && dispatch) {
      dispatch(loginSuccess({ token: res.data.token, user: res.data.user }));
      dispatch(
        loginUserSuccess({ token: res.data.token, user: res.data.user }),
      );
    }
  }
  return res;
};

/**
 *  Clear User Session
 */
export const clearSession = (options = {}) => {
  const { dispatch } = options;
  TokenManager.clearToken();

  // Update Redux state if dispatch provided
  if (isFunction(dispatch) && dispatch) {
    dispatch(storeLogout());
    dispatch(storeLogoutUser());
  }

  if (isBrowser) {
    localStorage.removeItem("userData");
    localStorage.removeItem("@secure.s.browserToken");
  }

  sessionStorage.clear();
};

/**
 * Logged-in user has an active membership?
 * @returns {boolean}
 */
export const hasActiveMembership = () => {};

/**
 * Logged-in user is signed up for the passed annual registration year
 * This checks the users payments data, looking for payments.linked_type="meeting", and
 * the associated payments.linked_table="registrations" && payments.linked_id=registrations.id && registrations.year=${year}
 * @param {number} year - The year for which to check registration
 * @returns {boolean}
 */
export const memberHasAnnualRegistration = (year) => {};

/**
 * Fetch and apply latest user profile and membership data
 * Updates localStorage and Redux state if dispatch provided
 * @param {Object} options - { dispatch, token, userData }
 * @returns {Promise<Object>} - Final user data
 */
export const applyUserData = async (options = {}) => {
  const { dispatch, token, userData } = options;
  const _token = token || TokenManager.getToken();
  try {
    const [_userProfile, _membership] = await Promise.all([
      getProfile(),
      getMembership(),
    ]);
    const serverUser = {
      ...userData,
      profile: _userProfile?.data?.data?.profile || {},
      membership: _membership?.data?.data || {},
    };
    const finalUserData = serverUser || userData;
    if (finalUserData) {
      if (isBrowser && serverUser) {
        localStorage.setItem("userData", JSON.stringify(serverUser));
      }
      if (dispatch) {
        dispatch(loginSuccess({ token: _token, user: finalUserData }));
        dispatch(loginUserSuccess({ token: _token, user: finalUserData }));
      }
    }
    log.debug("applyUserData() result:", finalUserData);
    return finalUserData;
  } catch (err) {
    log.error("applyUserData() error:", err);
    return userData;
  }
};
